import * as s from "../TableItemStyled";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import AccountSelects from "../AccountSelects";
import { intervalOptions } from "utils/options";
import { COLORS } from "styles/colors";
import { postStatCloudStorage } from "api/widgetAPI";
import {
  GetStatInterval,
  GetWidgetCloudStorageResponse,
} from "api/interfaces/widgetInterface.interface";
import { formatBytes } from "utils/functions";
import SelectComponent from "components/atoms/select/SelectComponent";
import TableItemHeader from "../TableItemHeader";
import { Layout } from "react-grid-layout";
import { StorageProps } from "components/blocks/storage/StorageLabel";
import NoDataChart from "components/templates/nopage/NoDataChart";
import { TableItemName } from "utils/WidgetUtil";
import OrgStackedBarChart from "pages/dashboard/stats/graph/BarGraph";
import Spinner from "components/atoms/loader/Spinner";

interface Props {
  layout: Layout;
  chartLayout: Layout[];
  onDeleteWidget: (key: TableItemName) => void;
  onLayoutChange: (layout: Layout[]) => void;
}

export default function CloudStorage(props: Props): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const [width, setWidth] = useState<number>(0);
  const chartRef = useRef<HTMLDivElement>(null);

  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [interval, setInterval] = useState<GetStatInterval>("daily");

  const [curStorage, setCurStorage] = useState<StorageProps>({
    usedStorage: 0,
    totalStorage: 0,
  });

  const getCloudStorage = useQuery(
    ["postStatCloudStorage", selectedAccount.accountId, accountIds, interval],
    () =>
      postStatCloudStorage({
        accountId: selectedAccount.accountId,
        interval: interval,
        payload: {
          accountIds,
          keyword: "",
        },
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: GetWidgetCloudStorageResponse) => {
        if (res.error !== 0 && res.result === undefined) {
        } else {
          // console.log(res.result, "cloudstorage");

          setCurStorage({
            usedStorage: res.result.usedStorage,
            totalStorage: res.result.totalStorage,
          });
        }
      },
    }
  );

  const onChangeAccount = (value: string[]) => {
    setAccountIds(value);
  };

  const onChangeInterval = (value: string) => {
    setInterval(value.toLowerCase() as GetStatInterval);
  };
  const data = useMemo(() => {
    return getCloudStorage.data?.result?.stats ?? [];
  }, [getCloudStorage.data?.result?.stats]);
  useLayoutEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    setWidth(el.clientWidth);

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? 0;
      setWidth(w);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [data]);

  return (
    <>
      <TableItemHeader
        layout={props.layout}
        chartLayout={props.chartLayout}
        onDeleteWidget={props.onDeleteWidget}
        onLayoutChange={props.onLayoutChange}
        isError={getCloudStorage.isError}
      />

      <s.Contents className="drag-cancel-content">
        <s.ContentWrapper>
          <s.ContentHeader>
            <div>
              <AccountSelects onChange={onChangeAccount} value={accountIds} />
              <div className="select-wrapper interval-select">
                <SelectComponent
                  value={intervalOptions.find(
                    (opt) => opt.value === interval.toUpperCase()
                  )}
                  options={intervalOptions.filter(
                    (opt) => opt.value !== "MONTHLY"
                  )}
                  onChange={onChangeInterval}
                  data-testid="dashboard-dashboard-list-selects-change-interval"
                />
              </div>
            </div>
            <div className="cloud-storage-wrapper">
              Cloud storage used:{" "}
              <strong className="cloud-storage">
                {curStorage.usedStorage === 0
                  ? 0
                  : formatBytes(curStorage.usedStorage)}{" "}
                /{" "}
                {curStorage.totalStorage === 0
                  ? 0
                  : formatBytes(curStorage.totalStorage)}
              </strong>
            </div>
          </s.ContentHeader>
          <s.ContentInner ref={chartRef}>
            <div className="bar-chart-wrapper">
              {getCloudStorage.isLoading ? (
                <Spinner />
              ) : data.length === 0 ||
                data.every(
                  (item: { usedStorage: number }) => item.usedStorage === 0
                ) ? (
                <NoDataChart />
              ) : (
                <OrgStackedBarChart
                  rows={data}
                  activeOrgs={["usedStorage"]}
                  width={width}
                  items={[
                    {
                      key: "usedStorage",
                      label: "Used Storage",
                      color: COLORS.BUTTONS2,
                    },
                  ]}
                  groupMode={"stacked"}
                  unit={"bytes"}
                  enableGridY
                />
              )}
            </div>
          </s.ContentInner>
        </s.ContentWrapper>
      </s.Contents>
    </>
  );
}
